import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import { cardInterestsService } from "../../services/card-interests.service";
import {
  EbayChannelConfig,
  FacebookChannelConfig,
  SnkrdunkChannelConfig,
  WhatsappChannelConfig,
} from "../../types/card-interest";

const ChannelConfigPage = () => {
  const { t } = useTranslation("common");

  const getErrorMessage = (error: unknown) =>
    error instanceof Error ? error.message : "Failed to save";

  const [facebookChannel, setFacebookChannel] =
    useState<FacebookChannelConfig | null>(null);
  const [ebayChannel, setEbayChannel] = useState<EbayChannelConfig | null>(null);
  const [snkrdunkChannel, setSnkrdunkChannel] =
    useState<SnkrdunkChannelConfig | null>(null);
  const [whatsappChannel, setWhatsappChannel] =
    useState<WhatsappChannelConfig | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isSavingFacebook, setIsSavingFacebook] = useState(false);
  const [isSavingEbay, setIsSavingEbay] = useState(false);
  const [isSavingSnkrdunk, setIsSavingSnkrdunk] = useState(false);
  const [isSavingWhatsapp, setIsSavingWhatsapp] = useState(false);
  const [error, setError] = useState("");

  const [fbEnabled, setFbEnabled] = useState(false);
  const [fbSurface, setFbSurface] = useState("marketplace");
  const [fbAuthMethod, setFbAuthMethod] = useState("cookie");
  const [fbAdapterMode, setFbAdapterMode] = useState("fetch");
  const [fbGroupScrapeMode, setFbGroupScrapeMode] = useState("search");
  const [fbGroupIds, setFbGroupIds] = useState("");
  const [fbGroupUrls, setFbGroupUrls] = useState("");
  const [fbPages, setFbPages] = useState("1");
  const [fbEntriesPerPage, setFbEntriesPerPage] = useState("50");
  const [fbCookie, setFbCookie] = useState("");

  const [ebayEnabled, setEbayEnabled] = useState(false);
  const [ebayAdapterMode, setEbayAdapterMode] = useState("browse");
  const [ebayBrowseEnv, setEbayBrowseEnv] = useState("production");
  const [ebayBrowseMarketplaceId, setEbayBrowseMarketplaceId] =
    useState("EBAY_US");

  const [snkEnabled, setSnkEnabled] = useState(true);
  const [snkPages, setSnkPages] = useState("1");
  const [snkEntriesPerPage, setSnkEntriesPerPage] = useState("50");
  const [snkHistoryPages, setSnkHistoryPages] = useState("10");
  const [snkHistoryPerPage, setSnkHistoryPerPage] = useState("50");
  const [snkIncludeActive, setSnkIncludeActive] = useState(true);
  const [snkIncludeSold, setSnkIncludeSold] = useState(true);
  const [snkIncludeHistory, setSnkIncludeHistory] = useState(true);
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setError("");
        const [fb, ebay, snk, whatsapp] = await Promise.all([
          cardInterestsService.getFacebookChannelConfig(),
          cardInterestsService.getEbayChannelConfig(),
          cardInterestsService.getSnkrdunkChannelConfig(),
          cardInterestsService.getWhatsappChannelConfig(),
        ]);
        setFacebookChannel(fb);
        setEbayChannel(ebay);
        setSnkrdunkChannel(snk);
        setWhatsappChannel(whatsapp);
      } catch (error: unknown) {
        setError(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!facebookChannel) return;
    setFbEnabled(facebookChannel.enabled === true);
    setFbSurface(facebookChannel.facebookSurface || "marketplace");
    setFbAuthMethod(facebookChannel.facebookAuthMethod || "cookie");
    setFbAdapterMode(facebookChannel.facebookAdapterMode || "fetch");
    setFbGroupScrapeMode(facebookChannel.facebookGroupScrapeMode || "search");
    setFbGroupIds(
      Array.isArray(facebookChannel.facebookGroupIds)
        ? facebookChannel.facebookGroupIds.join("\n")
        : "",
    );
    setFbGroupUrls(
      Array.isArray(facebookChannel.facebookGroupUrls)
        ? facebookChannel.facebookGroupUrls.join("\n")
        : "",
    );
    setFbPages(
      typeof facebookChannel.facebookPages === "number"
        ? String(facebookChannel.facebookPages)
        : "1",
    );
    setFbEntriesPerPage(
      typeof facebookChannel.facebookEntriesPerPage === "number"
        ? String(facebookChannel.facebookEntriesPerPage)
        : "50",
    );
  }, [facebookChannel]);

  useEffect(() => {
    if (!ebayChannel) return;
    setEbayEnabled(ebayChannel.enabled === true);
    setEbayAdapterMode(ebayChannel.ebayAdapterMode || "browse");
    setEbayBrowseEnv(ebayChannel.ebayBrowseEnv || "production");
    setEbayBrowseMarketplaceId(ebayChannel.ebayBrowseMarketplaceId || "EBAY_US");
  }, [ebayChannel]);

  useEffect(() => {
    if (!snkrdunkChannel) return;
    setSnkEnabled(snkrdunkChannel.enabled !== false);
    setSnkPages(String(snkrdunkChannel.snkrdunkPages ?? 1));
    setSnkEntriesPerPage(String(snkrdunkChannel.snkrdunkEntriesPerPage ?? 50));
    setSnkHistoryPages(String(snkrdunkChannel.snkrdunkHistoryPages ?? 10));
    setSnkHistoryPerPage(String(snkrdunkChannel.snkrdunkHistoryPerPage ?? 50));
    setSnkIncludeActive(snkrdunkChannel.snkrdunkIncludeActive !== false);
    setSnkIncludeSold(snkrdunkChannel.snkrdunkIncludeSold !== false);
    setSnkIncludeHistory(snkrdunkChannel.snkrdunkIncludeHistory !== false);
  }, [snkrdunkChannel]);

  useEffect(() => {
    if (!whatsappChannel) return;
    setWhatsappEnabled(whatsappChannel.enabled === true);
  }, [whatsappChannel]);

  const facebookCookieStatus = useMemo(() => {
    if (!facebookChannel) return null;
    return facebookChannel.facebookCookieSet ? "set" : "not_set";
  }, [facebookChannel]);

  const saveFacebook = async () => {
    const pagesNum = Number(fbPages);
    const perPageNum = Number(fbEntriesPerPage);
    const groupIds = fbGroupIds
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const groupUrls = fbGroupUrls
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    const payload: Partial<FacebookChannelConfig> & {
      facebookCookie?: string;
      facebookUsername?: string;
      facebookPassword?: string;
    } = {
      enabled: fbEnabled,
      facebookSurface: fbSurface,
      facebookAuthMethod: fbAuthMethod,
      facebookAdapterMode: fbAdapterMode,
      facebookGroupScrapeMode: fbGroupScrapeMode,
      facebookGroupIds: groupIds,
      facebookGroupUrls: groupUrls,
      facebookPages: Number.isFinite(pagesNum) ? pagesNum : 1,
      facebookEntriesPerPage: Number.isFinite(perPageNum) ? perPageNum : 50,
    };
    if (fbCookie.trim()) payload.facebookCookie = fbCookie.trim();

    try {
      setIsSavingFacebook(true);
      setError("");
      const updated = await cardInterestsService.updateFacebookChannelConfig(
        payload,
      );
      setFbCookie("");
      setFacebookChannel(updated);
    } catch (error: unknown) {
      setError(getErrorMessage(error));
    } finally {
      setIsSavingFacebook(false);
    }
  };

  const saveEbay = async () => {
    const payload: Partial<EbayChannelConfig> = {
      enabled: ebayEnabled,
      ebayAdapterMode,
      ebayBrowseEnv,
      ebayBrowseMarketplaceId: ebayBrowseMarketplaceId.trim() || null,
    };

    try {
      setIsSavingEbay(true);
      setError("");
      const updated = await cardInterestsService.updateEbayChannelConfig(payload);
      setEbayChannel(updated);
    } catch (error: unknown) {
      setError(getErrorMessage(error));
    } finally {
      setIsSavingEbay(false);
    }
  };

  const saveSnkrdunk = async () => {
    const pagesNum = Number(snkPages);
    const perPageNum = Number(snkEntriesPerPage);
    const histPagesNum = Number(snkHistoryPages);
    const histPerPageNum = Number(snkHistoryPerPage);

    const payload: Partial<SnkrdunkChannelConfig> = {
      enabled: snkEnabled,
      snkrdunkPages: Number.isFinite(pagesNum) ? pagesNum : 1,
      snkrdunkEntriesPerPage: Number.isFinite(perPageNum) ? perPageNum : 50,
      snkrdunkHistoryPages: Number.isFinite(histPagesNum) ? histPagesNum : 10,
      snkrdunkHistoryPerPage: Number.isFinite(histPerPageNum)
        ? histPerPageNum
        : 50,
      snkrdunkIncludeActive: snkIncludeActive,
      snkrdunkIncludeSold: snkIncludeSold,
      snkrdunkIncludeHistory: snkIncludeHistory,
    };

    try {
      setIsSavingSnkrdunk(true);
      setError("");
      const updated = await cardInterestsService.updateSnkrdunkChannelConfig(
        payload,
      );
      setSnkrdunkChannel(updated);
    } catch (error: unknown) {
      setError(getErrorMessage(error));
    } finally {
      setIsSavingSnkrdunk(false);
    }
  };

  const saveWhatsapp = async () => {
    try {
      setIsSavingWhatsapp(true);
      setError("");
      const updated = await cardInterestsService.updateWhatsappChannelConfig({
        enabled: whatsappEnabled,
      });
      setWhatsappChannel(updated);
    } catch (error: unknown) {
      setError(getErrorMessage(error));
    } finally {
      setIsSavingWhatsapp(false);
    }
  };

  return (
    <Layout title={t("channel_config")}>
      <div className="space-y-4 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {t("channel_config")}
            </h1>
          </div>
          {error && (
            <div className="mt-3 p-3 rounded-md bg-red-50 text-red-600 border border-red-100">
              {error}
            </div>
          )}
          {isLoading && (
            <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              {t("loading")}
            </div>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
          <Card className="p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t("facebook_channel")}
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={saveFacebook}
                isLoading={isSavingFacebook}
              >
                {t("save")}
              </Button>
            </div>

            <div className="mt-3 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={fbEnabled}
                  onChange={(e) => setFbEnabled(e.target.checked)}
                />
                {t("facebook_enabled")}
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("facebook_surface")}
                  </div>
                  <select
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={fbSurface}
                    onChange={(e) => setFbSurface(e.target.value)}
                  >
                    <option value="marketplace">{t("marketplace")}</option>
                    <option value="groups">{t("groups")}</option>
                    <option value="both">{t("both")}</option>
                  </select>
                </div>

                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("facebook_group_scrape_mode")}
                  </div>
                  <select
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={fbGroupScrapeMode}
                    onChange={(e) => setFbGroupScrapeMode(e.target.value)}
                  >
                    <option value="search">
                      {t("facebook_group_scrape_search")}
                    </option>
                    <option value="feed">{t("facebook_group_scrape_feed")}</option>
                  </select>
                </div>

                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("facebook_adapter")}
                  </div>
                  <select
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={fbAdapterMode}
                    onChange={(e) => setFbAdapterMode(e.target.value)}
                  >
                    <option value="fetch">{t("fetch")}</option>
                    <option value="playwright">{t("playwright")}</option>
                  </select>
                </div>

                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("facebook_auth")}
                  </div>
                  <select
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={fbAuthMethod}
                    onChange={(e) => setFbAuthMethod(e.target.value)}
                  >
                    <option value="public">{t("public")}</option>
                    <option value="cookie">{t("cookie")}</option>
                    <option value="credentials">{t("credentials")}</option>
                  </select>
                </div>

                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("facebook_pages")}
                  </div>
                  <input
                    value={fbPages}
                    onChange={(e) => setFbPages(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("facebook_per_page")}
                  </div>
                  <input
                    value={fbEntriesPerPage}
                    onChange={(e) => setFbEntriesPerPage(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div className="sm:col-span-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("facebook_group_ids")}
                  </div>
                  <textarea
                    value={fbGroupIds}
                    onChange={(e) => setFbGroupIds(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={3}
                  />
                </div>

                <div className="sm:col-span-2">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("facebook_group_urls")}
                  </div>
                  <textarea
                    value={fbGroupUrls}
                    onChange={(e) => setFbGroupUrls(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={3}
                  />
                </div>

                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t("facebook_cookie")}
                    </div>
                    {facebookCookieStatus === "set" ? (
                      <div className="text-xs text-green-600 dark:text-green-300">
                        {t("cookie_set")}
                      </div>
                    ) : facebookCookieStatus === "not_set" ? (
                      <div className="text-xs text-gray-400">
                        {t("cookie_not_set")}
                      </div>
                    ) : null}
                  </div>
                  <textarea
                    value={fbCookie}
                    onChange={(e) => setFbCookie(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={4}
                    placeholder={t("facebook_cookie_placeholder")}
                  />
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t("ebay_channel")}
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={saveEbay}
                isLoading={isSavingEbay}
              >
                {t("save")}
              </Button>
            </div>

            <div className="mt-3 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={ebayEnabled}
                  onChange={(e) => setEbayEnabled(e.target.checked)}
                />
                {t("ebay_enabled")}
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("ebay_adapter_mode")}
                  </div>
                  <select
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={ebayAdapterMode}
                    onChange={(e) => setEbayAdapterMode(e.target.value)}
                  >
                    <option value="browse">{t("ebay_mode_browse")}</option>
                    <option value="api">{t("ebay_mode_finding")}</option>
                    <option value="html">{t("ebay_mode_html")}</option>
                  </select>
                </div>

                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("ebay_browse_env")}
                  </div>
                  <select
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    value={ebayBrowseEnv}
                    onChange={(e) => setEbayBrowseEnv(e.target.value)}
                  >
                    <option value="production">{t("production")}</option>
                    <option value="sandbox">{t("sandbox")}</option>
                  </select>
                </div>

                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("ebay_browse_marketplace")}
                  </div>
                  <input
                    value={ebayBrowseMarketplaceId}
                    onChange={(e) => setEbayBrowseMarketplaceId(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* eBay credentials are managed via environment variables now */}
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t("snkrdunk_channel")}
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={saveSnkrdunk}
                isLoading={isSavingSnkrdunk}
              >
                {t("save")}
              </Button>
            </div>

            <div className="mt-3 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={snkEnabled}
                  onChange={(e) => setSnkEnabled(e.target.checked)}
                />
                {t("snkrdunk_enabled")}
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("snkrdunk_pages")}
                  </div>
                  <input
                    value={snkPages}
                    onChange={(e) => setSnkPages(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("snkrdunk_per_page")}
                  </div>
                  <input
                    value={snkEntriesPerPage}
                    onChange={(e) => setSnkEntriesPerPage(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("snkrdunk_history_pages")}
                  </div>
                  <input
                    value={snkHistoryPages}
                    onChange={(e) => setSnkHistoryPages(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t("snkrdunk_history_per_page")}
                  </div>
                  <input
                    value={snkHistoryPerPage}
                    onChange={(e) => setSnkHistoryPerPage(e.target.value)}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={snkIncludeActive}
                    onChange={(e) => setSnkIncludeActive(e.target.checked)}
                  />
                  {t("include_active")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={snkIncludeSold}
                    onChange={(e) => setSnkIncludeSold(e.target.checked)}
                  />
                  {t("include_sold")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={snkIncludeHistory}
                    onChange={(e) => setSnkIncludeHistory(e.target.checked)}
                  />
                  {t("include_history")}
                </label>
              </div>
            </div>
          </Card>

          <Card className="p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                WhatsApp
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={saveWhatsapp}
                isLoading={isSavingWhatsapp}
              >
                {t("save")}
              </Button>
            </div>

            <div className="mt-3 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={whatsappEnabled}
                  onChange={(e) => setWhatsappEnabled(e.target.checked)}
                />
                WhatsApp enabled
              </label>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Enables WhatsApp client startup globally. WhatsApp source records
                are now used only for configuration such as chat prefix and
                history limit.
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default ChannelConfigPage;
